/**
 * Aggregate Query Service
 * 
 * Application service for aggregate read operations following CQRS pattern.
 * Optimized for data retrieval and presentation.
 */

import { injectable, inject } from 'inversify';
import { Aggregate } from '../../domain/entities/Aggregate';
import type { IAggregateRepository } from '../../../meta-model-design/domain/ports/IAggregateRepository';
import type { IBoundedContextRepository } from '../../../meta-model-design/domain/ports/IBoundedContextRepository';
import { AggregateDto } from '../queries/GetAggregateQuery';
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';

export interface AggregateListOptions {
  boundedContextId?: string;
  domainId?: string;
  includeComplexityAnalysis?: boolean;
  nameFilter?: string;
  complexityThreshold?: number;
}

export interface AggregateAnalysisDto {
  aggregate: AggregateDto;
  relationships: {
    referencedAggregates: string[];
    referencingAggregates: string[];
  };
  designMetrics: {
    complexityScore: number;
    cohesionScore: number;
    entityCount: number;
    valueObjectCount: number;
    businessRuleCount: number;
  };
  recommendations: string[];
}

@injectable()
export class AggregateQueryService {
  constructor(
    @inject(DDD_SYMBOLS.IAggregateRepository) private aggregateRepository: IAggregateRepository,
    @inject(DDD_SYMBOLS.IBoundedContextRepository) private boundedContextRepository: IBoundedContextRepository
  ) {}

  async getAggregate(aggregateId: string): Promise<AggregateDto | null> {
    const aggregate = await this.aggregateRepository.findById(aggregateId);
    if (!aggregate) {
      return null;
    }

    return this.toDto(aggregate);
  }

  async listAggregates(options: AggregateListOptions = {}): Promise<AggregateDto[]> {
    let aggregates: Aggregate[] = [];

    if (options.boundedContextId) {
      aggregates = await this.aggregateRepository.findByBoundedContext(options.boundedContextId);
    } else if (options.domainId) {
      // Get all bounded contexts for the domain, then all aggregates
      const boundedContextsResult = await this.boundedContextRepository.findByDomainId(options.domainId);
      const allAggregates = await Promise.all(
        boundedContextsResult.contexts.map((bc: any) => this.aggregateRepository.findByBoundedContext(bc.id))
      );
      aggregates = allAggregates.flat();
    } else {
      // This would need a findAll method in the repository
      throw new Error('Either boundedContextId or domainId must be provided');
    }

    // Apply filters
    if (options.nameFilter) {
      aggregates = aggregates.filter(agg => 
        agg.name.toLowerCase().includes(options.nameFilter!.toLowerCase())
      );
    }

    if (options.complexityThreshold) {
      aggregates = aggregates.filter(agg => 
        agg.getComplexityScore() >= options.complexityThreshold!
      );
    }

    return aggregates.map(agg => this.toDto(agg));
  }

  async getAggregateAnalysis(aggregateId: string): Promise<AggregateAnalysisDto | null> {
    const aggregate = await this.aggregateRepository.findById(aggregateId);
    if (!aggregate) {
      return null;
    }

    // Get relationships
    const relationships = await this.aggregateRepository.getAggregateRelationships(aggregate.boundedContextId);
    const aggregateRelationships = relationships.find(r => r.aggregateId === aggregateId);

    // Calculate design metrics
    const designMetrics = {
      complexityScore: aggregate.getComplexityScore(),
      cohesionScore: aggregate.getCohesionScore(),
      entityCount: 1 + aggregate.childEntities.length, // +1 for root entity
      valueObjectCount: aggregate.valueObjects.length,
      businessRuleCount: aggregate.businessInvariantIds.length
    };

    // Generate recommendations
    const recommendations = this.generateRecommendations(aggregate, designMetrics);

    return {
      aggregate: this.toDto(aggregate),
      relationships: {
        referencedAggregates: aggregateRelationships?.referencedAggregates || [],
        referencingAggregates: aggregateRelationships?.referencingAggregates || []
      },
      designMetrics,
      recommendations
    };
  }

  async searchAggregates(searchTerm: string, boundedContextId?: string): Promise<AggregateDto[]> {
    const aggregates = await this.aggregateRepository.findByName(searchTerm, boundedContextId);
    return aggregates.map(agg => this.toDto(agg));
  }

  async getHighComplexityAggregates(complexityThreshold: number, boundedContextId?: string): Promise<AggregateDto[]> {
    const aggregates = await this.aggregateRepository.findHighComplexityAggregates(complexityThreshold, boundedContextId);
    return aggregates.map(agg => this.toDto(agg));
  }

  async getAggregatesUsingValueObject(valueObjectName: string): Promise<AggregateDto[]> {
    const aggregates = await this.aggregateRepository.findAggregatesUsingValueObject(valueObjectName);
    return aggregates.map(agg => this.toDto(agg));
  }

  async getAggregatesEmittingEvent(eventName: string): Promise<AggregateDto[]> {
    const aggregates = await this.aggregateRepository.findAggregatesEmittingEvent(eventName);
    return aggregates.map(agg => this.toDto(agg));
  }

  private toDto(aggregate: Aggregate): AggregateDto {
    return {
      aggregateId: aggregate.id,
      boundedContextId: aggregate.boundedContextId,
      name: aggregate.name,
      description: aggregate.description,
      responsibility: aggregate.responsibility,
      rootEntity: aggregate.rootEntity,
      childEntities: aggregate.childEntities,
      valueObjects: aggregate.valueObjects,
      businessInvariantIds: aggregate.businessInvariantIds,
      domainEventIds: aggregate.domainEventIds,
      ubiquitousLanguageTerms: aggregate.ubiquitousLanguageTerms,
      complexityScore: aggregate.getComplexityScore(),
      cohesionScore: aggregate.getCohesionScore(),
      createdAt: aggregate.createdAt,
      updatedAt: aggregate.updatedAt
    };
  }

  private generateRecommendations(aggregate: Aggregate, metrics: any): string[] {
    const recommendations: string[] = [];

    if (metrics.complexityScore > 75) {
      recommendations.push('High complexity detected - consider splitting this aggregate into smaller units');
    }

    if (metrics.cohesionScore < 50) {
      recommendations.push('Low cohesion detected - review if all entities truly belong together');
    }

    if (metrics.entityCount > 7) {
      recommendations.push('Large number of entities - consider if this aggregate has too many responsibilities');
    }

    if (metrics.businessRuleCount > 10) {
      recommendations.push('Many business rules - consider using specification pattern for complex validation');
    }

    if (aggregate.valueObjects.length === 0) {
      recommendations.push('No value objects found - consider if some properties could be modeled as value objects');
    }

    if (aggregate.domainEventIds.length === 0) {
      recommendations.push('No domain events found - consider what important business events this aggregate should publish');
    }

    if (recommendations.length === 0) {
      recommendations.push('Aggregate design looks well-balanced for its current scope');
    }

    return recommendations;
  }
}